/**
 * ═══════════════════════════════════════════════════════════════
 *  FOLD REGISTRY — Phase 2 Sidebar Fold Plan
 * ═══════════════════════════════════════════════════════════════
 *
 * Canonical map of every sidebar route that has been "folded" into
 * a parent surface. Folded routes STAY LIVE — the registry only
 * controls sidebar visibility, never routing.
 *
 * Rules enforced by foldRegistry.test.ts:
 *  1. Every folded route must declare a fold target.
 *  2. Every removed sidebar entry must have registry coverage.
 *  3. ROLE_ALLOWED_ROUTES is never narrowed by folding.
 *  4. Roles in `keepStandaloneFor` continue to see the route in
 *     their sidebar — fold semantics do NOT apply to them.
 *
 * Fold semantics: embed-only (no auto-redirects). Users still
 * reach the route via deep link, handoff banner, or registry CTA.
 */

import type { UserRole } from "./RoleContext";

/**
 * The kind of surface that absorbs the folded route.
 *
 * - `tab`         — embedded tab on a parent detail/profile page
 * - `panel`       — inline panel on a parent surface
 * - `widget`      — dashboard widget that surfaces the same data
 * - `action`      — launched via button/CTA (form, modal)
 * - `queue`       — primary queue view a role lives in
 * - `report-link` — surfaced in the Reports list
 */
export type FoldType = "tab" | "panel" | "widget" | "action" | "queue" | "report-link";

export interface FoldEntry {
  /** The route being folded (must remain live). */
  route: string;
  /** Where the feature now lives in the UI. */
  foldType: FoldType;
  /** Human-readable parent surface (e.g. "Customer Profile → Messages tab"). */
  foldTarget: string;
  /** Route a user would navigate to in order to reach the fold target. */
  foldEntryRoute: string;
  /** Roles that should continue seeing this in their sidebar (fold does NOT apply). */
  keepStandaloneFor: UserRole[];
  /** Short rationale for the fold, surfaced in support docs. */
  reason: string;
}

/**
 * The canonical fold table. Source of truth for sidebar removals.
 */
export const FOLD_REGISTRY: FoldEntry[] = [
  {
    route: "/messages",
    foldType: "tab",
    foldTarget: "Customer Profile → Messages tab",
    foldEntryRoute: "/customers",
    // Call center lives in messages as a primary queue and cannot reach /customers.
    keepStandaloneFor: ["call_center"],
    reason: "Conversations belong with the customer they reference, except for call_center where it's a primary queue.",
  },
  {
    route: "/follow-ups",
    foldType: "widget",
    foldTarget: "Sales Command → My Follow-Ups widget",
    foldEntryRoute: "/command-dashboard",
    // Call center + sales reps both rely on follow-ups as a primary queue
    // and don't have /command-dashboard access.
    keepStandaloneFor: ["call_center", "sales"],
    reason: "Follow-ups widget lives on the Command Dashboard; call_center + sales keep the standalone queue.",
  },
  {
    route: "/issues",
    foldType: "tab",
    foldTarget: "Job Detail → Issues tab",
    foldEntryRoute: "/jobs",
    // Operations/warehouse/service-adjacent roles live in the global queue.
    keepStandaloneFor: [
      "warehouse_manager",
      "head_of_installation",
      "procurement_manager",
      "inventory_manager",
      "ops_manager",
    ],
    reason: "Issues are job-scoped for most roles; ops/warehouse keep the global queue.",
  },
  {
    route: "/service-tickets",
    foldType: "tab",
    foldTarget: "Customer Profile → Tickets tab",
    foldEntryRoute: "/customers",
    keepStandaloneFor: [
      "warehouse_manager",
      "head_of_installation",
      "procurement_manager",
      "inventory_manager",
    ],
    reason: "Service tickets follow the customer; warehouse/service keep the standalone queue.",
  },
  {
    route: "/appointment-results",
    foldType: "widget",
    foldTarget: "Sales Command → Recent Outcomes (View All)",
    foldEntryRoute: "/command-dashboard",
    // Sales reps use Appointment Results as a primary surface and cannot reach /command-dashboard.
    keepStandaloneFor: ["sales"],
    reason: "Recent outcomes surface inline on the Command Dashboard; sales reps keep direct access.",
  },
  {
    route: "/qa-form",
    foldType: "action",
    foldTarget: "Job Detail → Install QA action",
    foldEntryRoute: "/jobs",
    keepStandaloneFor: [],
    reason: "QA form is an action launched from a job, not a destination.",
  },
  {
    route: "/issue-form",
    foldType: "action",
    foldTarget: "Global Friction button (header)",
    foldEntryRoute: "/",
    keepStandaloneFor: [],
    reason: "Issue reporting is a global action, not a navigated page.",
  },
  {
    route: "/dispatch-intelligence",
    foldType: "tab",
    foldTarget: "Dispatch Board → Intelligence tab",
    foldEntryRoute: "/scheduling-dispatch",
    keepStandaloneFor: [],
    reason: "Intelligence belongs alongside the dispatch surface it informs.",
  },
  {
    route: "/lead-integrity",
    foldType: "tab",
    foldTarget: "Lead Sources → Integrity tab",
    foldEntryRoute: "/lead-sources",
    keepStandaloneFor: [],
    reason: "Integrity is a lens on lead sources, not a separate module.",
  },
  {
    route: "/disposition-report",
    foldType: "report-link",
    foldTarget: "Reports list → Disposition Report",
    foldEntryRoute: "/reports",
    keepStandaloneFor: [],
    reason: "One of many reports — belongs in the Reports list.",
  },
  {
    route: "/pipeline-intelligence",
    foldType: "tab",
    foldTarget: "Lead Performance → Pipeline Intelligence link",
    foldEntryRoute: "/growth-channels",
    // sales_manager has /pipeline-intelligence but not /growth-channels — keep standalone access.
    keepStandaloneFor: ["sales_manager"],
    reason: "Pipeline intelligence is reachable from Lead Performance; sales_manager keeps standalone access since /growth-channels is not in their route set.",
  },
  {
    route: "/marketing-attribution",
    foldType: "report-link",
    foldTarget: "Lead Performance → Attribution link",
    foldEntryRoute: "/growth-channels",
    // sales_manager has /marketing-attribution but not /growth-channels — keep standalone access.
    keepStandaloneFor: ["sales_manager"],
    reason: "Job-level + per-rep attribution drill-down reachable via the Attribution chip in Lead Performance. sales_manager keeps standalone access since /growth-channels is not in their route set. Route stays live.",
  },
  {
    route: "/earnings-intelligence",
    foldType: "tab",
    foldTarget: "Finance → Earnings tab",
    foldEntryRoute: "/accounting",
    keepStandaloneFor: [],
    reason: "Earnings analysis lives inside the finance surface.",
  },
  {
    route: "/revenue-intelligence",
    foldType: "tab",
    foldTarget: "Finance → Revenue tab",
    foldEntryRoute: "/accounting",
    keepStandaloneFor: [],
    reason: "Revenue analysis lives inside the finance surface.",
  },
  {
    route: "/portals",
    foldType: "action",
    foldTarget: "Customers → Portal Access action",
    foldEntryRoute: "/customers",
    keepStandaloneFor: [],
    reason: "Portal access is granted per-customer, not browsed centrally.",
  },
  {
    route: "/rep-scorecards",
    foldType: "widget",
    foldTarget: "Command Dashboard → Rep Scorecards section",
    foldEntryRoute: "/command-dashboard",
    // Hidden from sidebar for sales_manager/super_admin/admin — they reach it
    // via Command Dashboard's "Open Rep Scorecards" CTA or per-row links.
    // Sales reps keep the standalone entry: they have route access but cannot
    // reach /command-dashboard, so the fold target is unreachable for them.
    keepStandaloneFor: ["sales"],
    reason: "Folded into Command Dashboard → Rep Performance section. Routes stay live for sales_manager/super_admin/admin via embed CTA; sales reps keep standalone access since /command-dashboard is unreachable for them.",
  },
];

/**
 * Lookup map: route → entry.
 */
export const FOLD_BY_ROUTE: Record<string, FoldEntry> = Object.fromEntries(
  FOLD_REGISTRY.map((e) => [e.route, e])
);

/**
 * Returns true if `route` should be HIDDEN from the sidebar for `role`.
 * Returns false if the route is not folded, or if the role is in
 * `keepStandaloneFor` for that route.
 *
 * Routes themselves remain live regardless of this result.
 */
export function isRouteFoldedForRole(route: string, role: UserRole): boolean {
  const entry = FOLD_BY_ROUTE[route];
  if (!entry) return false;
  return !entry.keepStandaloneFor.includes(role);
}

/**
 * For a given role, list every route folded out of its sidebar.
 */
export function getFoldedRoutesForRole(role: UserRole): FoldEntry[] {
  return FOLD_REGISTRY.filter((e) => !e.keepStandaloneFor.includes(role));
}

/**
 * For a given route, return its fold entry (or null if not folded).
 * Useful for rendering "where did this go?" hints.
 */
export function getFoldEntry(route: string): FoldEntry | null {
  return FOLD_BY_ROUTE[route] ?? null;
}
